import { camelize, decamelize, pascalize } from 'fast-case';
import {
  type CountryCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { capitalize } from 'remeda';
import slugifyRaw from 'slugify';
import { truncateMiddle } from 'truncate-middle';

const startsWithUuidRegex = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}/i;

export { capitalize };

/**
 * Convert a string to camelCase.
 * Does not convert UUIDs, because that would break them.
 */
export const toCamelCase = (variableName: string) =>
  typeof variableName === 'string' && !startsWithUuidRegex.test(variableName)
    ? camelize(variableName)
    : variableName;

/**
 * Convert a string to PascalCase.
 * Does not convert UUIDs, because that would break them.
 */
export const toPascalCase = (variableName: string) =>
  typeof variableName === 'string' && !startsWithUuidRegex.test(variableName)
    ? pascalize(variableName)
    : variableName;

/**
 * Convert a string to snake_case.
 * Does not convert UUIDs, because that would break them.
 */
export const toSnakeCase = (variableName: string) =>
  typeof variableName === 'string' && !startsWithUuidRegex.test(variableName)
    ? decamelize(variableName)
    : variableName;

/**
 * Convert a string to Title Case.
 */
export const toTitleCase = (variableName: string) =>
  typeof variableName === 'string'
    ? variableName.replace(/^./, (string) => string.toUpperCase())
    : variableName;

/**
 * Truncates a string to a specified maximum length and adds an ellipsis if truncated.
 *
 * @param value - The string to be truncated.
 * @param maxLength - The maximum length of the string. Defaults to 26.
 * @param charactersToPreserveAtEnd - The number of characters to preserve at the end of the string. Will truncate the middle of the string if set. Defaults to 0.
 * @param ellipsis - The marker added when truncating. Defaults to `…`.
 * @return The truncated string.
 */
export const truncate = (
  value: string,
  maxLength = 26,
  charactersToPreserveAtEnd = 0,
  ellipsis = '…',
) => {
  if (!value) {
    return '';
  }

  return truncateMiddle(
    value,
    maxLength - charactersToPreserveAtEnd,
    charactersToPreserveAtEnd,
    ellipsis,
  );
};

/**
 * Convert a string to a slug by replacing spaces with dashes and making it lowercase.
 * Uses the `slugify` package under the hood.
 * @param text - The input string to be slugified.
 * @param options - The options for slugifying the string.
 * @returns The slugified string.
 */
export const slugify = (text = '', options = {}) =>
  typeof text === 'string'
    ? slugifyRaw(text, {
        locale: 'de',
        lower: true,
        replacement: '-',
        strict: true,
        ...options,
      })
    : '';

/**
 * Format a phone number for display.
 *
 * Uses libphonenumber-js (Google's phone metadata) to correctly split
 * country code, area code, and subscriber number.
 *
 * - Replaces a leading "00" with "+" before parsing
 * - Numbers without a "+" prefix are parsed as numbers of `defaultCountry` (DE)
 * - Returns the original value unchanged when it cannot be parsed
 *
 * @example
 * formatPhoneNumber('004989123456')   // '+49 89 123456'
 * formatPhoneNumber('+498131354490')  // '+49 8131 354490'
 * formatPhoneNumber('08131354490')    // '+49 8131 354490'
 * formatPhoneNumber('089 123456')     // '+49 89 123456'
 * formatPhoneNumber('0664 1234567', 'AT') // '+43 664 1234567'
 */
export function formatPhoneNumber(
  phone: string,
  defaultCountry: CountryCode = 'DE',
): string {
  if (!phone.trim()) {
    return phone;
  }

  // Normalise leading "00" to "+" so the parser recognises it as international
  let normalised = phone.replaceAll(/[\s-]/g, '');
  if (normalised.startsWith('00')) {
    normalised = `+${normalised.slice(2)}`;
  }

  // The default country resolves local numbers (e.g. 089…)
  const parsed = parsePhoneNumberFromString(normalised, defaultCountry);

  if (!parsed) {
    return phone;
  }

  return parsed.formatInternational();
}

export function extractNameFromEmail(email: string) {
  // Only extract names when the email looks complete (has @ with domain)
  const [localPart, domain] = email.split('@');

  if (!localPart || !domain) {
    return {
      firstName: null,
      lastName: null,
    };
  }

  const parts = localPart.split('.');

  const firstName = capitalize(parts[0] ?? '');
  const lastName = parts
    .slice(1)
    .map((part) => capitalize(part))
    .join(' ');

  return {
    firstName: firstName || null,
    lastName: lastName || null,
    name: [firstName, lastName].filter(Boolean).join(' ') || null,
  };
}

/**
 * Trims the string and collapses every run of whitespace to a single space.
 */
export function collapseWhitespace(value: string): string {
  return value.replaceAll(/\s+/g, ' ').trim();
}
